import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import PublicProfile from "@/pages/PublicProfile";
import TryBoard from "@/pages/TryBoard";
import GuestBoard from "@/pages/GuestBoard";
import SceneBackground from "@/components/SceneBackground";
import InstallPrompt from "@/components/InstallPrompt";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="stone-bg App flex items-center justify-center">
        <p className="osrs-title text-2xl osrs-shadow" data-testid="app-loading">Loading Grand Exchange...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Animated scene backdrop only on the logged-in dashboard; static (flicker-free) everywhere else.
// Gated on an authenticated user so first-time visitors never mount (then unmount) the animated
// background during the auth check — that remount was the first-load flicker.
const RouteBackground = () => {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  return <SceneBackground animated={pathname === "/" && !!user && !loading} />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteBackground />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#3a3428",
                color: "#FFCF3F",
                border: "2px solid #0f0d09",
                fontFamily: "Cinzel, serif",
                borderRadius: "2px",
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<PublicOnly><Auth /></PublicOnly>} />
            <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
            <Route path="/u/:rsn" element={<PublicProfile />} />
            <Route path="/try/:rsn" element={<TryBoard />} />
            <Route path="/guest/:rsn" element={<GuestBoard />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
